/**
 * Value chain — sticky viewport + scrubbed timeline (no GSAP pin).
 *
 * @package HelloElementorChild
 */

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

var valueChainInstances = new WeakMap();
var valueChainResizeBound = false;
// Keep in sync with the $bp-3xl breakpoint in _value-chain.scss: below this the
// component uses the stacked mobile layout, above it the pinned desktop slider.
var DESKTOP_MIN = 1600;
var TAB_WIDTH_FALLBACK = 80;
var SCROLL_DESKTOP_MIN = 300;
var SCROLL_INTRO = 0.14;
var SCROLL_OUTRO = 0.06;
var INTRO_HOLD = 2;
var STEP_HOLD = 1;
var TRANSITION = 1.5;

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function isValueChainDesktop() {
  return window.matchMedia('(min-width: ' + DESKTOP_MIN + 'px)').matches;
}

function getScrollDesktopLength(root) {
  var parsed = parseInt(root.getAttribute('data-scroll-desktop') || '600', 10);

  return Number.isNaN(parsed) ? 600 : Math.max(SCROLL_DESKTOP_MIN, parsed);
}

function setActiveNav(root, index) {
  var count = parseInt(root.getAttribute('data-step-count') || '0', 10);
  var safeIndex = Math.max(0, Math.min(count - 1, index));

  root.querySelectorAll('[data-camst-value-chain-nav-btn]').forEach(function (btn) {
    var btnIndex = parseInt(btn.getAttribute('data-index') || '0', 10);
    var isActive = btnIndex === safeIndex;

    btn.classList.toggle('is-active', isActive);
    btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
  });

  var indicator = root.querySelector('[data-camst-value-chain-nav-indicator]');

  if (indicator) {
    indicator.style.transform = 'translateX(' + safeIndex * 100 + '%)';
  }

  root._camstVcActiveIndex = safeIndex;
}

function getStepLayoutMetrics(root) {
  var stepsContainer = root.querySelector('[data-camst-value-chain-steps]');
  var tab = root.querySelector('.camst-value-chain__tab');

  if (!stepsContainer || !tab) {
    return {
      tabWidth: TAB_WIDTH_FALLBACK,
      containerWidth: 0,
    };
  }

  var tabRect = tab.getBoundingClientRect();
  var containerRect = stepsContainer.getBoundingClientRect();
  var tabWidth = Math.round(tabRect.width || TAB_WIDTH_FALLBACK);
  var containerWidth = Math.round(containerRect.width || 0);

  return {
    tabWidth: tabWidth > 0 ? tabWidth : TAB_WIDTH_FALLBACK,
    containerWidth: containerWidth > 0 ? containerWidth : 0,
  };
}

function applyStepLayoutMetrics(root) {
  var metrics = getStepLayoutMetrics(root);

  root.style.setProperty('--vc-tab-width', metrics.tabWidth + 'px');
  root.style.setProperty('--vc-steps-width', metrics.containerWidth + 'px');

  return metrics;
}

function getStepRestX(tabWidth, index) {
  return index * tabWidth;
}

function resetValueChainVisualState(root) {
  root.querySelectorAll('[data-camst-value-chain-step]').forEach(function (step) {
    gsap.set(step, { clearProps: 'transform,x,y,width' });
  });

  root.querySelectorAll('[data-camst-value-chain-overlay]').forEach(function (overlay) {
    gsap.set(overlay, { clearProps: 'opacity' });
  });

  root.style.removeProperty('--vc-tab-width');
  root.style.removeProperty('--vc-steps-width');
}

function killValueChainInstance(instance) {
  if (!instance) {
    return;
  }

  if (instance.matchMedia) {
    instance.matchMedia.revert();
    instance.matchMedia = null;
  }

  if (instance.scrollTrigger) {
    instance.scrollTrigger.kill();
    instance.scrollTrigger = null;
  }

  if (instance.timeline) {
    instance.timeline.kill();
    instance.timeline = null;
  }
}

function mapScrollToTimeline(scrollProgress) {
  var range = 1 - SCROLL_INTRO - SCROLL_OUTRO;

  if (scrollProgress <= SCROLL_INTRO) {
    return 0;
  }

  if (scrollProgress >= 1 - SCROLL_OUTRO) {
    return 1;
  }

  return (scrollProgress - SCROLL_INTRO) / range;
}

function mapTimelineToScroll(timelineProgress) {
  var range = 1 - SCROLL_INTRO - SCROLL_OUTRO;

  return SCROLL_INTRO + timelineProgress * range;
}

function getStepIndexFromTimeline(timeline, stepCount) {
  var time = timeline.progress() * timeline.totalDuration();
  var active = 0;

  for (var i = stepCount - 1; i >= 0; i -= 1) {
    var label = timeline.labels['step-' + i];

    if (typeof label === 'number' && time >= label - 0.001) {
      active = i;
      break;
    }
  }

  return active;
}

function syncValueChainFromScroll(root, scrollTrigger, timeline, stepCount) {
  if (!scrollTrigger || !timeline) {
    return;
  }

  var mapped = mapScrollToTimeline(scrollTrigger.progress);

  timeline.progress(mapped);

  // Perf: `setActiveNav` fa query + scritture sul DOM. Durante lo scrub viene
  // invocata ~ogni frame, quindi la eseguiamo solo quando l'indice attivo
  // cambia davvero (non ad ogni micro-avanzamento della timeline).
  var activeIndex = getStepIndexFromTimeline(timeline, stepCount);

  if (root._camstVcActiveIndex !== activeIndex) {
    root._camstVcActiveIndex = activeIndex;
    setActiveNav(root, activeIndex);
  }
}

function buildValueChainTimeline(root, stepCount) {
  var steps = root.querySelectorAll('[data-camst-value-chain-step]');
  var metrics = applyStepLayoutMetrics(root);
  var tabWidth = metrics.tabWidth;
  var containerWidth = metrics.containerWidth;

  resetValueChainVisualState(root);
  applyStepLayoutMetrics(root);

  // Perf: la larghezza degli step resta fissa alla larghezza piena del
  // contenitore e animiamo solo `x` (transform). Il bordo destro eccedente
  // viene ritagliato dal pin-viewport (`overflow: hidden`) e il contenuto è
  // ancorato a sinistra, quindi il risultato è identico all'animazione di
  // `width` ma senza reflow per frame — solo compositing su GPU.
  var fullWidth = containerWidth > 0 ? containerWidth : '100%';

  steps.forEach(function (step, index) {
    gsap.set(step, {
      x: index === 0 ? 0 : fullWidth,
      width: fullWidth,
      force3D: true,
    });
  });

  var timeline = gsap.timeline({ paused: true });

  timeline.addLabel('step-0');
  timeline.to({}, { duration: INTRO_HOLD });

  for (var index = 0; index < stepCount - 1; index += 1) {
    var nextIndex = index + 1;
    var nextStep = root.querySelector('[data-camst-value-chain-step="' + nextIndex + '"]');
    var currentOverlay = root.querySelector(
      '[data-camst-value-chain-step="' + index + '"] [data-camst-value-chain-overlay]'
    );
    var restX = getStepRestX(tabWidth, nextIndex);

    if (nextStep) {
      timeline.fromTo(
        nextStep,
        {
          x: fullWidth,
        },
        {
          x: restX,
          duration: TRANSITION,
          ease: 'power2.inOut',
        }
      );
    }

    if (currentOverlay) {
      timeline.to(currentOverlay, { opacity: 0.6, duration: 0.5 }, '<');
    }

    timeline.addLabel('step-' + nextIndex);
    timeline.to({}, { duration: STEP_HOLD });
  }

  return timeline;
}

function scrollToValueChainStep(root, index, reducedMotion) {
  var instance = valueChainInstances.get(root);

  if (!instance || !instance.timeline || !instance.scrollTrigger) {
    return;
  }

  var timeline = instance.timeline;
  var labelTime = timeline.labels['step-' + index];

  if (typeof labelTime !== 'number') {
    return;
  }

  var timelineProgress = labelTime / timeline.totalDuration();
  var st = instance.scrollTrigger;
  var currentScroll = window.scrollY || window.pageYOffset;
  var range = st.end - st.start;
  var targetScroll;

  if (index === 0) {
    var introEnd = st.start + SCROLL_INTRO * range;

    if (currentScroll >= st.start && currentScroll <= introEnd) {
      timeline.progress(0);
      setActiveNav(root, 0);
      return;
    }

    targetScroll = st.start;
  } else {
    var scrollProgress = mapTimelineToScroll(timelineProgress);
    targetScroll = st.start + scrollProgress * range;
  }

  if (Math.abs(currentScroll - targetScroll) < 32) {
    timeline.progress(timelineProgress);
    setActiveNav(root, index);
    return;
  }

  window.scrollTo({
    top: targetScroll,
    behavior: reducedMotion || index === 0 ? 'auto' : 'smooth',
  });
  setActiveNav(root, index);
}

function bindValueChainNav(root) {
  if (root._camstValueChainNavBound) {
    return;
  }

  root._camstValueChainNavBound = true;
  var reducedMotion = prefersReducedMotion();

  root.addEventListener('click', function (event) {
    var navBtn = event.target.closest('[data-camst-value-chain-nav-btn], [data-camst-value-chain-tab-jump]');

    if (!navBtn || !root.contains(navBtn)) {
      return;
    }

    event.preventDefault();

    var index = parseInt(navBtn.getAttribute('data-index') || '0', 10);
    scrollToValueChainStep(root, index, reducedMotion);
  });
}

function applyTrackHeight(root) {
  var track = root.querySelector('[data-camst-value-chain-track]');

  if (!track) {
    return;
  }

  if (isValueChainDesktop()) {
    var scrollLength = getScrollDesktopLength(root);
    track.style.setProperty('--vc-track-lg', 100 + scrollLength + 'vh');
    return;
  }

  track.style.removeProperty('--vc-track-lg');
}

function createDesktopScrollTrigger(root, timeline, stepCount) {
  var track = root.querySelector('[data-camst-value-chain-track]');

  if (!track) {
    return null;
  }

  var triggerId = 'valueChainTrigger-' + (root.id || root.dataset.stepCount || '0');

  return ScrollTrigger.create({
    trigger: track,
    start: 'top top',
    end: 'bottom bottom',
    scrub: 0.6,
    invalidateOnRefresh: true,
    id: triggerId,
    onUpdate: function (self) {
      syncValueChainFromScroll(root, self, timeline, stepCount);
    },
    onRefresh: function (self) {
      syncValueChainFromScroll(root, self, timeline, stepCount);
    },
  });
}

function setupDesktopTimeline(root, stepCount) {
  var instance = valueChainInstances.get(root) || {};
  var reducedMotion = prefersReducedMotion();

  if (reducedMotion) {
    resetValueChainVisualState(root);
    setActiveNav(root, 0);
    return instance;
  }

  var mm = gsap.matchMedia();
  instance.matchMedia = mm;

  mm.add('(min-width: ' + DESKTOP_MIN + 'px)', function () {
    var track = root.querySelector('[data-camst-value-chain-track]');

    if (!track) {
      return function () {};
    }

    applyTrackHeight(root);

    var timeline = buildValueChainTimeline(root, stepCount);
    var scrollTrigger = createDesktopScrollTrigger(root, timeline, stepCount);

    if (!scrollTrigger) {
      timeline.kill();
      return function () {};
    }

    instance.timeline = timeline;
    instance.scrollTrigger = scrollTrigger;
    instance.layoutMetrics = getStepLayoutMetrics(root);
    valueChainInstances.set(root, instance);

    ScrollTrigger.refresh();
    syncValueChainFromScroll(root, scrollTrigger, timeline, stepCount);

    return function () {
      scrollTrigger.kill();
      timeline.kill();
      resetValueChainVisualState(root);
      instance.timeline = null;
      instance.scrollTrigger = null;
    };
  });

  mm.add('(max-width: ' + (DESKTOP_MIN - 1) + 'px)', function () {
    if (instance.scrollTrigger) {
      instance.scrollTrigger.kill();
      instance.scrollTrigger = null;
    }

    if (instance.timeline) {
      instance.timeline.kill();
      instance.timeline = null;
    }

    resetValueChainVisualState(root);
    applyTrackHeight(root);
    setActiveNav(root, 0);

    return function () {};
  });

  return instance;
}

function rebuildValueChainRoot(root, forceTimelineRebuild) {
  var stepCount = parseInt(root.getAttribute('data-step-count') || '0', 10);

  if (!stepCount) {
    return;
  }

  var instance = valueChainInstances.get(root);

  if (!instance || !instance.matchMedia) {
    bindValueChainNav(root);
    instance = setupDesktopTimeline(root, stepCount);
    valueChainInstances.set(root, instance);
    return;
  }

  applyTrackHeight(root);

  if (isValueChainDesktop()) {
    var metrics = getStepLayoutMetrics(root);
    var layoutChanged =
      forceTimelineRebuild ||
      !instance.layoutMetrics ||
      instance.layoutMetrics.tabWidth !== metrics.tabWidth ||
      instance.layoutMetrics.containerWidth !== metrics.containerWidth;

    if (layoutChanged && instance.scrollTrigger && instance.timeline) {
      var timelineProgress = instance.timeline.progress();
      var activeIndex = getStepIndexFromTimeline(instance.timeline, stepCount);

      killValueChainInstance(instance);
      valueChainInstances.delete(root);
      bindValueChainNav(root);
      instance = setupDesktopTimeline(root, stepCount);
      valueChainInstances.set(root, instance);

      if (instance.timeline && instance.scrollTrigger) {
        instance.timeline.progress(timelineProgress);
        syncValueChainFromScroll(root, instance.scrollTrigger, instance.timeline, stepCount);
        setActiveNav(root, activeIndex);
      }

      if (instance) {
        instance.layoutMetrics = metrics;
      }

      ScrollTrigger.refresh();
      return;
    }
  }

  applyStepLayoutMetrics(root);
  ScrollTrigger.refresh();

  if (instance) {
    instance.layoutMetrics = getStepLayoutMetrics(root);
  }

  if (instance.scrollTrigger && instance.timeline) {
    syncValueChainFromScroll(root, instance.scrollTrigger, instance.timeline, stepCount);
    return;
  }

  if (isValueChainDesktop()) {
    killValueChainInstance(instance);
    valueChainInstances.delete(root);
    bindValueChainNav(root);
    instance = setupDesktopTimeline(root, stepCount);
    instance.layoutMetrics = getStepLayoutMetrics(root);
    valueChainInstances.set(root, instance);
  }
}

function bindValueChainViewportRefresh() {
  if (valueChainResizeBound) {
    return;
  }

  valueChainResizeBound = true;

  var refreshQueued = false;

  function queueRefresh() {
    if (refreshQueued) {
      return;
    }

    refreshQueued = true;

    window.requestAnimationFrame(function () {
      refreshQueued = false;
      refreshCamstValueChain();
    });
  }

  window.addEventListener('resize', queueRefresh, { passive: true });
  window.addEventListener('orientationchange', queueRefresh, { passive: true });
}

export function initCamstValueChain(scope) {
  var roots = (scope || document).querySelectorAll('[data-camst-value-chain]');

  roots.forEach(function (root) {
    if (valueChainInstances.has(root)) {
      rebuildValueChainRoot(root);
      return;
    }

    var stepCount = parseInt(root.getAttribute('data-step-count') || '0', 10);

    if (!stepCount) {
      return;
    }

    bindValueChainNav(root);
    setActiveNav(root, 0);

    var instance = setupDesktopTimeline(root, stepCount);
    valueChainInstances.set(root, instance);
  });

  bindValueChainViewportRefresh();
  ScrollTrigger.refresh();
}

export function teardownCamstValueChain(scope) {
  var roots = (scope || document).querySelectorAll('[data-camst-value-chain]');

  roots.forEach(function (root) {
    resetValueChainVisualState(root);
    killValueChainInstance(valueChainInstances.get(root));
    valueChainInstances.delete(root);
    root._camstValueChainNavBound = false;
  });
}

export function refreshCamstValueChain() {
  document.querySelectorAll('[data-camst-value-chain]').forEach(function (root) {
    rebuildValueChainRoot(root, true);
  });

  ScrollTrigger.refresh();
}
